import React, { useEffect, useMemo, useState } from "react";
import Navbar from "@/components/Navbar";
import { api } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Plus, Save, Trash2, Package } from "lucide-react";

// Super-admin catalogue: verticals + products + which verticals each product
// belongs to. Adding a product here immediately lights it up on the Program
// Setup page — no code deploy needed.
export default function AdminProducts() {
  const [catalog, setCatalog] = useState(null);
  const [editing, setEditing] = useState(null); // product being edited (id or "new")
  const [form, setForm] = useState({ name: "", short_name: "", description: "", sort_order: 100, active: true, vertical_ids: [] });

  const load = async () => {
    try {
      const { data } = await api.get("/onboarding/catalog");
      setCatalog(data);
    } catch (e) { toast.error("Failed to load catalog"); }
  };
  useEffect(() => { load(); }, []);

  const mapByProduct = useMemo(() => {
    const m = {};
    for (const x of catalog?.mappings || []) {
      if (!m[x.product_id]) m[x.product_id] = new Set();
      m[x.product_id].add(x.vertical_id);
    }
    return m;
  }, [catalog]);

  const startNew = () => {
    setEditing("new");
    setForm({ name: "", short_name: "", description: "", sort_order: 100, active: true, vertical_ids: [] });
  };
  const startEdit = (p) => {
    setEditing(p.id);
    setForm({
      name: p.name || "",
      short_name: p.short_name || "",
      description: p.description || "",
      sort_order: p.sort_order || 100,
      active: !!p.active,
      vertical_ids: Array.from(mapByProduct[p.id] || []),
    });
  };
  const toggleVert = (vid) => {
    setForm((f) => {
      const s = new Set(f.vertical_ids);
      if (s.has(vid)) s.delete(vid); else s.add(vid);
      return { ...f, vertical_ids: Array.from(s) };
    });
  };
  const save = async () => {
    if (!form.name.trim()) { toast.error("Name required"); return; }
    try {
      if (editing === "new") {
        await api.post("/admin/onboarding/products", form);
        toast.success("Product added");
      } else {
        await api.put(`/admin/onboarding/products/${editing}`, form);
        toast.success("Product updated");
      }
      setEditing(null);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };
  const disable = async (p) => {
    if (!window.confirm(`Disable "${p.name}"? Existing onboarding records keep their history — new setups just won't offer it.`)) return;
    try {
      await api.delete(`/admin/onboarding/products/${p.id}`);
      toast.success("Product disabled");
      await load();
    } catch { toast.error("Failed"); }
  };

  if (!catalog) {
    return (
      <div className="min-h-screen bg-[#F8F9FA]">
        <Navbar />
        <main className="max-w-5xl mx-auto px-4 sm:px-6 py-8"><div className="text-sm text-[#6C757D]">Loading…</div></main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      <Navbar />
      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-8" data-testid="admin-products-page">
        <div className="flex items-end justify-between gap-4 mb-6">
          <div>
            <div className="text-[10px] uppercase tracking-[0.28em] font-bold text-[#D4AF37]">Onboarding catalogue</div>
            <h1 className="font-display text-4xl font-black text-[#0B132B] leading-none mt-1">Products & Verticals</h1>
            <p className="text-sm text-[#6C757D] mt-2">Add, rename, disable, or reorder the products offered on the Program Setup page. No code deploy needed.</p>
          </div>
          <Button onClick={startNew} className="h-11 rounded-sm bg-[#0B132B] hover:bg-[#1C2541] text-white" data-testid="admin-products-new">
            <Plus size={14} className="mr-2"/> New product
          </Button>
        </div>

        {editing && (
          <Card className="p-5 border-[#E9ECEF] rounded-sm mb-6" data-testid="admin-products-editor">
            <div className="text-xs uppercase tracking-[0.22em] font-bold text-[#0B132B] mb-3">
              {editing === "new" ? "New product" : "Edit product"}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="sm:col-span-1">
                <Label className="text-[10px] uppercase tracking-[0.16em] font-bold text-[#0B132B]">Name</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 h-10 rounded-sm border-[#E9ECEF]" data-testid="admin-product-name"/>
              </div>
              <div className="sm:col-span-1">
                <Label className="text-[10px] uppercase tracking-[0.16em] font-bold text-[#0B132B]">Short name</Label>
                <Input value={form.short_name} onChange={(e) => setForm({ ...form, short_name: e.target.value })} className="mt-1 h-10 rounded-sm border-[#E9ECEF]" data-testid="admin-product-short-name"/>
              </div>
              <div className="sm:col-span-2">
                <Label className="text-[10px] uppercase tracking-[0.16em] font-bold text-[#0B132B]">Description</Label>
                <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1 h-10 rounded-sm border-[#E9ECEF]" data-testid="admin-product-desc"/>
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-[0.16em] font-bold text-[#0B132B]">Sort order</Label>
                <Input type="number" value={form.sort_order} onChange={(e) => setForm({ ...form, sort_order: parseInt(e.target.value) || 100 })} className="mt-1 h-10 rounded-sm border-[#E9ECEF]" data-testid="admin-product-sort"/>
              </div>
              <div className="flex items-end">
                <label className="flex items-center gap-2 h-10 text-sm text-[#0B132B] cursor-pointer">
                  <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} data-testid="admin-product-active"/>
                  Active
                </label>
              </div>
            </div>
            <div className="mt-4">
              <Label className="text-[10px] uppercase tracking-[0.16em] font-bold text-[#0B132B] mb-2 block">Applicable verticals</Label>
              <div className="flex flex-wrap gap-2">
                {(catalog.verticals || []).map((v) => {
                  const on = form.vertical_ids.includes(v.id);
                  return (
                    <button key={v.id} type="button" onClick={() => toggleVert(v.id)}
                      className={`h-9 px-3 rounded-sm border text-xs uppercase tracking-[0.14em] font-bold transition-colors ${
                        on ? "bg-[#0B132B] border-[#0B132B] text-white" : "bg-white border-[#E9ECEF] text-[#0B132B] hover:border-[#0B132B]/40"
                      }`}
                      data-testid={`admin-product-vertical-${v.key}`}>
                      {v.name}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="mt-5 flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setEditing(null)} className="h-10 rounded-sm">Cancel</Button>
              <Button onClick={save} className="h-10 rounded-sm bg-[#0B132B] hover:bg-[#1C2541] text-white" data-testid="admin-product-save">
                <Save size={14} className="mr-2"/> Save
              </Button>
            </div>
          </Card>
        )}

        <Card className="border-[#E9ECEF] rounded-sm overflow-hidden" data-testid="admin-products-table">
          <div className="grid grid-cols-[1fr_120px_1fr_60px_100px] gap-2 px-4 py-2 bg-[#F8F9FA] border-b border-[#E9ECEF] text-[10px] uppercase tracking-[0.16em] font-bold text-[#6C757D]">
            <div>Product</div><div>Short</div><div>Verticals</div><div>Sort</div><div className="text-right">Actions</div>
          </div>
          {(catalog.products || []).map((p) => {
            const verts = Array.from(mapByProduct[p.id] || [])
              .map(vid => (catalog.verticals || []).find(v => v.id === vid)?.name)
              .filter(Boolean).join(", ");
            return (
              <div key={p.id} className={`grid grid-cols-[1fr_120px_1fr_60px_100px] gap-2 px-4 py-3 items-center border-b border-[#E9ECEF] last:border-b-0 text-sm ${p.active ? "" : "opacity-50"}`} data-testid={`admin-product-row-${p.key}`}>
                <div>
                  <div className="font-bold text-[#0B132B] flex items-center gap-2"><Package size={12} className="text-[#D4AF37]"/> {p.name}</div>
                  {p.description && <div className="text-[11px] text-[#6C757D]">{p.description}</div>}
                </div>
                <div className="text-xs text-[#6C757D]">{p.short_name || "—"}</div>
                <div className="text-xs text-[#6C757D]">{verts || <span className="italic">none</span>}</div>
                <div className="text-xs text-[#6C757D]">{p.sort_order}</div>
                <div className="flex gap-1 justify-end">
                  <Button size="sm" variant="outline" onClick={() => startEdit(p)} className="h-8 rounded-sm text-xs" data-testid={`admin-product-edit-${p.key}`}>Edit</Button>
                  {p.active && (
                    <Button size="sm" variant="outline" onClick={() => disable(p)} className="h-8 rounded-sm text-xs text-red-600 hover:bg-red-50" data-testid={`admin-product-disable-${p.key}`}>
                      <Trash2 size={12}/>
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </Card>
      </main>
    </div>
  );
}
